import { Grid } from '@mui/material';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  RadialLinearScale,
  Tooltip,
  registerables,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  IAgGridTblPayload,
  localStorageUtils,
  SliderCarousel,
  PermissionTypes,
  IRole,
  objectUtils,
  authUtils,
  SDK_CONSTANTS,
  GenericContext,
  SDK_EVENTS_CONSTANTS,
} from 'fso-fincrime-sdk-ui';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TaskListContext from '../../../contexts/TaskListContext';
import {
  GQL_CHARTS_DATA,
  GQL_DASHBOARD_WORKLOAD_CARD_DATA,
} from '../../../service/graphql/dashboard-workload.service';
import { IChart } from '../../../ts/entities/chart/IChart';
import { CONSTANTS } from '../../../utils/constants/constants';
import ChartContainer from '../../chart/ChartContainer';

import './ChartCarousel.scss';

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  LineController,
  PointElement,
  RadialLinearScale,
  ArcElement,
  ChartDataLabels,
  Tooltip,
  Legend,
  ...registerables
);

const ChartDisplay = (): JSX.Element => {
  const { t } = useTranslation();
  const { selectedLanguage } = useContext(GenericContext);

  const [activeRole, setActiveRole] = useState<IRole>(
    localStorageUtils.getSavedItem(SDK_CONSTANTS.LOCAL_STORAGE_KEYS.USER.ACTIVE_ROLE)
  );
  const canSeeCards = authUtils.checkUserPermissions(PermissionTypes.CHART_CARD);
  const canSeeTaskCharts = authUtils.checkUserPermissions(PermissionTypes.CHART_TASK);

  const [charts, setCharts] = useState<IChart[]>([]);
  const [taskListPayload, setTaskListPayload] = useState<IAgGridTblPayload | undefined>(
    localStorageUtils.getSavedItem(SDK_CONSTANTS.LOCAL_STORAGE_KEYS.TASK_LIST_PAYLOAD)
  );

  const {
    data: cardData,
    loading: cardLoading,
    error: cardError,
    refetch: cardRefetch,
  } = GQL_DASHBOARD_WORKLOAD_CARD_DATA(
    localStorageUtils.getSavedItem(SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.WORKFLOW_NAME),
    localStorageUtils.getSavedItem(
      SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.WORKFLOW_VERSION
    ),
    !canSeeCards
  );

  const {
    data: chartData,
    loading: chartLoading,
    error: chartError,
    refetch: chartRefetch,
  } = GQL_CHARTS_DATA(CONSTANTS.CHART.REQUEST, taskListPayload, canSeeTaskCharts);

  useEffect(() => {
    const handleTaskListPayloadUpdated = (e: Event): void => {
      setTaskListPayload(objectUtils.deepCopy((e as CustomEvent).detail));
    };
    const handleActiveRoleUpdated = (e: Event): void => {
      setActiveRole(objectUtils.deepCopy((e as CustomEvent).detail));
    };

    document.addEventListener(
      SDK_EVENTS_CONSTANTS.TASK_LIST_PAYLOAD_UPDATED,
      handleTaskListPayloadUpdated
    );
    document.addEventListener(SDK_EVENTS_CONSTANTS.ACTIVE_ROLE_UPDATED, handleActiveRoleUpdated);

    return (): void => {
      document.removeEventListener(
        SDK_EVENTS_CONSTANTS.TASK_LIST_PAYLOAD_UPDATED,
        handleTaskListPayloadUpdated
      );
      document.removeEventListener(
        SDK_EVENTS_CONSTANTS.ACTIVE_ROLE_UPDATED,
        handleActiveRoleUpdated
      );
    };
  }, []);

  const getChartTitle = (chartTitle: string): string => {
    if (chartTitle === 'Workload By Task') {
      return t('CHARTS.WORKLOAD_BY_TASK.TITLE');
    }
    if (chartTitle === 'Procedural Timelines') {
      return t('CHARTS.PROCEDURAL_TIMELINES.TITLE');
    }
    return chartTitle;
  };

  const getChartDatasetLabel = (chartTitle: string, datasetLabel: string): string => {
    if (chartTitle === 'Workload By Task') {
      if (datasetLabel === 'Not Started') {
        return t('CHARTS.WORKLOAD_BY_TASK.LABELS.NOT_STARTED');
      }
      if (datasetLabel === 'In Progress') {
        return t('CHARTS.WORKLOAD_BY_TASK.LABELS.IN_PROGRESS');
      }
    }
    if (chartTitle === 'Procedural Timelines') {
      if (datasetLabel === 'Missed') {
        return t('CHARTS.PROCEDURAL_TIMELINES.LABELS.MISSED');
      }
      if (datasetLabel === 'At Risk') {
        return t('CHARTS.PROCEDURAL_TIMELINES.LABELS.AT_RISK');
      }
      if (datasetLabel === 'On Track') {
        return t('CHARTS.PROCEDURAL_TIMELINES.LABELS.ON_TRACK');
      }
      if (datasetLabel === 'None') {
        return t('CHARTS.PROCEDURAL_TIMELINES.LABELS.NONE');
      }
    }
    return datasetLabel;
  };

  useEffect(() => {
    const newCharts: IChart[] = [];

    if (canSeeTaskCharts) {
      const charts = chartData?.chartsQuery ?? [];
      if (!chartError && !!charts.length) {
        for (const data of charts) {
          const { datasets } = data;
          for (const dataset of datasets) {
            dataset.label = getChartDatasetLabel(data.title, dataset.label);
          }
          data.title = getChartTitle(data.title);
          newCharts.push({
            data,
            chartType: data.type,
            loading: chartLoading,
          });
        }
      }
      if (!chartError && chartLoading && !charts.length) {
        newCharts.push({
          data: undefined,
          chartType: undefined,
          loading: chartLoading,
        });
      }
    }

    if (!cardError && canSeeCards) {
      newCharts.push({
        data: cardData?.cardViewDetails,
        loading: cardLoading,
        chartType: 'cards',
      });
    }

    setCharts(newCharts);
  }, [chartData, cardData, activeRole, selectedLanguage]);

  useEffect(() => {
    chartRefetch();
    cardRefetch();
  }, [activeRole, selectedLanguage]);

  return (
    <>
      {taskListPayload ? (
        <TaskListContext.Provider value={taskListPayload}>
          <Grid container spacing={2} className="chart-carousel">
            {cardError == undefined || chartError == undefined ? (
              <SliderCarousel
                toShow={charts.map((chart, chartIndex) => (
                  <ChartContainer
                    data={chart.data}
                    chartType={chart.chartType}
                    loading={chart.loading}
                    isDatePickerEnabled={chart.isDatePickerEnabled}
                    key={chartIndex}
                  />
                ))}
              />
            ) : (
              <p>{t('ERROR_FETCHING')}</p>
            )}
          </Grid>
        </TaskListContext.Provider>
      ) : (
        <p>{t('ERROR_FETCHING')}</p>
      )}
    </>
  );
};

export default ChartDisplay;
